import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, alpha, useTheme, Chip, LinearProgress,
  Skeleton, Button, Tab, Tabs, Avatar, CircularProgress,
  Popover,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Visibility as ViewsIcon,
  FavoriteBorder as LikesIcon,
  ChatBubbleOutline as CommentsIcon,
  Share as SharesIcon,
  EmojiEvents as TrophyIcon,
  PlayCircle as VideoIcon,
  Image as ImageIcon,
  Article as TextIcon,
  BarChart as BarChartIcon,
  CalendarToday as CalIcon,
  CheckCircle as CheckIcon,
  Bolt as BoltIcon,
  Timeline as TimelineIcon,
  Psychology as PsychologyIcon,
  Lock as LockIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { PLATFORM_CONFIG, PLATFORM_ICONS } from './post/PLATFORM_CONFIG';
import ConnectPlatformsCTA from './ConnectPlatformsCTA';
import { rankApi } from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const PLATFORM_COLORS = Object.fromEntries(
  Object.entries(PLATFORM_CONFIG).map(([k, v]) => [k, v.color])
);

function platformColor(p) { return PLATFORM_COLORS[p?.toLowerCase()] || '#6366f1'; }
function platformLabel(p) { return PLATFORM_CONFIG[p?.toLowerCase()]?.label || p; }
function platformIcon(p)  { return PLATFORM_ICONS[p?.toLowerCase()] || null; }

function fmtNum(n) {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const MEDIA_ICONS = {
  VIDEO: <VideoIcon sx={{ fontSize: 13 }} />,
  IMAGE: <ImageIcon sx={{ fontSize: 13 }} />,
  REEL:  <VideoIcon sx={{ fontSize: 13 }} />,
  CAROUSEL_ALBUM: <ImageIcon sx={{ fontSize: 13 }} />,
};
function mediaIcon(type) { return MEDIA_ICONS[type] || <TextIcon sx={{ fontSize: 13 }} />; }

// ── Skeleton ──────────────────────────────────────────────────────────────────
function PageSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ flex: 1, borderRadius: '14px' }} />)}
      </Box>
      <Grid container spacing={2.5}>
        {[1,2,3,4,5,6].map(i => <Grid key={i} size={{ xs:12, sm:6, md:4 }}><Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} /></Grid>)}
      </Grid>
    </Box>
  );
}

// ── Summary stat cards ────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, sub }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 120, p: 2.25, borderRadius: '14px',
      bgcolor: alpha(color, 0.06), border: `1px solid ${alpha(color, 0.14)}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        {React.cloneElement(icon, { sx: { fontSize: 15, color } })}
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color, lineHeight: 1 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: 0.4 }}>{sub}</Typography>}
    </Box>
  );
}

// ── Tab panel ─────────────────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Score breakdown popover ───────────────────────────────────────────────────
function ScoreBadge({ post, color }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [anchor, setAnchor] = useState(null);

  const likes    = post.likes    || 0;
  const comments = post.comments || 0;
  const shares   = post.shares   || 0;
  const views    = post.views    || 0;

  // Score formula: likes + comments×2 + shares×3
  const fromLikes    = likes;
  const fromComments = comments * 2;
  const fromShares   = shares   * 3;
  const total        = fromLikes + fromComments + fromShares;

  const rows = [
    { label: 'Likes',    formula: `${fmtNum(likes)} × 1`,   value: fromLikes,    color: '#f43f5e' },
    { label: 'Comments', formula: `${fmtNum(comments)} × 2`, value: fromComments, color: '#6366f1' },
    { label: 'Shares',   formula: `${fmtNum(shares)} × 3`,   value: fromShares,   color: '#10b981' },
  ];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
      {/* Score badge */}
      <Box sx={{
        px: 1, py: 0.2, borderRadius: '5px',
        bgcolor: alpha(color, 0.1),
        display: 'flex', alignItems: 'center',
      }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color, lineHeight: 1 }}>
          Score {fmtNum(post.score)}
        </Typography>
      </Box>

      {/* Info icon */}
      <Box
        onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{
          display: 'flex', alignItems: 'center', cursor: 'pointer',
          color: alpha(color, 0.6),
          '&:hover': { color },
          transition: 'color 0.15s',
        }}
      >
        <InfoIcon sx={{ fontSize: 13 }} />
      </Box>

      {/* Popover */}
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5, borderRadius: '10px', p: 0, overflow: 'hidden',
              background: isDark ? '#1e1e2e' : '#fff',
              border: `1px solid ${alpha(color, 0.2)}`,
              boxShadow: `0 8px 32px ${alpha(color, 0.15)}`,
              minWidth: 220,
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.25,
          background: alpha(color, isDark ? 0.15 : 0.07),
          borderBottom: `1px solid ${alpha(color, 0.15)}`,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <InfoIcon sx={{ fontSize: 14, color }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color }}>
            How this score is calculated
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Formula rows */}
          {rows.map(r => (
            <Box key={r.label} sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              mb: 1,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '2px', bgcolor: r.color, flexShrink: 0,
                }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  {r.label}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                  ({r.formula})
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: r.color }}>
                +{fmtNum(r.value)}
              </Typography>
            </Box>
          ))}

          {/* Total bar */}
          <Box sx={{
            mt: 1.5, pt: 1.25, borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.primary' }}>
              Total Score
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color }}>
              {fmtNum(total)}
            </Typography>
          </Box>

          {/* Views note if available */}
          {views > 0 && (
            <Typography sx={{
              fontSize: '0.65rem', color: 'text.disabled', mt: 1.25,
              lineHeight: 1.5,
            }}>
              Views ({fmtNum(views)}) are tracked separately and not included in the engagement score.
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1: Top Posts
// ─────────────────────────────────────────────────────────────────────────────
function TopPostCard({ post, rank }) {
  const theme  = useTheme();
  const color  = platformColor(post.platform);
  const icon   = platformIcon(post.platform);
  const [imgFail, setImgFail] = useState(false);

  return (
    <Box sx={{
      borderRadius: '16px',
      border: `1.5px solid ${rank <= 3 ? alpha(color, 0.35) : alpha(theme.palette.divider, 0.1)}`,
      overflow: 'hidden',
      background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : '#fff',
      transition: 'all 0.2s ease',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 32px ${alpha(color, 0.18)}`, borderColor: alpha(color, 0.4) },
    }}>
      {/* Thumbnail */}
      <Box sx={{ position: 'relative', aspectRatio: '16/9', bgcolor: alpha(color, 0.08), overflow: 'hidden' }}>
        {post.media_url && !imgFail ? (
          <img src={post.media_url} alt="" onError={() => setImgFail(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon ? React.cloneElement(icon, { sx: { fontSize: 40, color: alpha(color, 0.3) } }) : null}
          </Box>
        )}
        {/* Rank badge */}
        <Box sx={{
          position: 'absolute', top: 8, left: 8,
          width: 28, height: 28, borderRadius: '8px',
          bgcolor: rank <= 3 ? color : alpha(theme.palette.background.paper, 0.9),
          color: rank <= 3 ? '#fff' : theme.palette.text.secondary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 900, backdropFilter: 'blur(4px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          {rank <= 3 ? ['🥇','🥈','🥉'][rank-1] : `#${rank}`}
        </Box>
        {/* Media type pill */}
        <Chip
          icon={mediaIcon(post.media_type)}
          label={post.media_type || 'POST'}
          size="small"
          sx={{
            position: 'absolute', bottom: 8, right: 8,
            height: 20, fontSize: '0.58rem', fontWeight: 800,
            bgcolor: alpha('#000', 0.55), color: '#fff', backdropFilter: 'blur(4px)',
            '& .MuiChip-icon': { color: '#fff !important', ml: '4px' },
          }}
        />
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Platform row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, color }}>
            {icon ? React.cloneElement(icon, { sx: { fontSize: 15 } }) : null}
          </Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {platformLabel(post.platform)}
          </Typography>
          {post.timestamp && (
            <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled', ml: 'auto' }}>
              {new Date(post.timestamp).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {/* Caption */}
        {post.caption && (
          <Typography sx={{
            fontSize: '0.8rem', lineHeight: 1.4, color: 'text.secondary',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            mb: 1.25,
          }}>
            {post.caption}
          </Typography>
        )}

        {/* Metrics */}
        <Box sx={{ display: 'flex', gap: 2, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.07)}` }}>
          {[
            { icon: <ViewsIcon sx={{ fontSize: 12 }} />,    val: post.views,    show: post.views > 0 },
            { icon: <LikesIcon sx={{ fontSize: 12 }} />,    val: post.likes,    show: post.likes > 0 },
            { icon: <CommentsIcon sx={{ fontSize: 12 }} />, val: post.comments, show: post.comments > 0 },
            { icon: <SharesIcon sx={{ fontSize: 12 }} />,   val: post.shares,   show: post.shares > 0 },
          ].filter(m => m.show).map((m, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'text.secondary' }}>
              {m.icon}
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color }}>
                {fmtNum(m.val)}
              </Typography>
            </Box>
          ))}
          {post.score > 0 && (
            <ScoreBadge post={post} color={color} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2: Platform Analysis (content types + avg metrics)
// ─────────────────────────────────────────────────────────────────────────────
function PlatformAnalysisCard({ stat }) {
  const theme = useTheme();
  const color = platformColor(stat.platform);
  const icon  = platformIcon(stat.platform);
  const cfg   = PLATFORM_CONFIG[stat.platform?.toLowerCase()] || {};

  const metrics = [
    { label: 'Views/post',    value: stat.post_count ? Math.round(stat.total_views / stat.post_count) : 0,    color: '#0ea5e9' },
    { label: 'Likes/post',    value: stat.post_count ? Math.round(stat.total_likes / stat.post_count) : 0,    color: '#ec4899' },
    { label: 'Comments/post', value: stat.post_count ? Math.round(stat.total_comments / stat.post_count) : 0, color: '#f59e0b' },
    { label: 'Shares/post',   value: stat.post_count ? Math.round(stat.total_shares / stat.post_count) : 0,   color: '#10b981' },
  ];
  const maxMetric = Math.max(...metrics.map(m => m.value), 1);

  const types    = stat.content_types || {};
  const totalTyped = Object.values(types).reduce((a, b) => a + b, 0) || 1;

  return (
    <Box sx={{
      borderRadius: '20px', overflow: 'hidden',
      background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : '#fff',
      border: `1.5px solid ${alpha(color, 0.2)}`,
      boxShadow: `0 4px 20px ${alpha(color, 0.1)}`,
    }}>
      <Box sx={{ height: 4, background: cfg.gradient || color }} />
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 40, height: 40, borderRadius: '12px' }}>
            {icon ? React.cloneElement(icon, { sx: { fontSize: 20 } }) : null}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.95rem' }}>{cfg.label || stat.platform}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{stat.post_count} posts · {stat.engagement_rate.toFixed(1)}% eng rate</Typography>
          </Box>
          <Chip
            label={`Avg ${fmtNum(stat.eng_per_post)} eng`}
            size="small"
            sx={{ ml: 'auto', height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: alpha(color, 0.1), color, border: 'none' }}
          />
        </Box>

        {/* Avg metric bars */}
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25 }}>
          Avg per post
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
          {metrics.map(m => (
            <Box key={m.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600, width: 90, flexShrink: 0 }}>{m.label}</Typography>
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={Math.min(100, (m.value / maxMetric) * 100)}
                  sx={{ height: 5, borderRadius: 3, bgcolor: alpha(m.color, 0.1),
                    '& .MuiLinearProgress-bar': { bgcolor: m.color, borderRadius: 3 } }} />
              </Box>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: m.color, width: 36, textAlign: 'right' }}>
                {fmtNum(m.value)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Content type breakdown */}
        {Object.keys(types).length > 0 && (
          <>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
              Content mix
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {Object.entries(types).sort((a,b) => b[1]-a[1]).map(([type, count]) => (
                <Chip
                  key={type}
                  icon={mediaIcon(type)}
                  label={`${type} ${Math.round(count / totalTyped * 100)}%`}
                  size="small"
                  sx={{
                    height: 22, fontSize: '0.62rem', fontWeight: 800,
                    bgcolor: alpha(color, stat.best_type === type ? 0.18 : 0.06),
                    color, border: stat.best_type === type ? `1px solid ${alpha(color, 0.4)}` : 'none',
                    '& .MuiChip-icon': { color: `${color} !important`, fontSize: '12px !important' },
                  }}
                />
              ))}
            </Box>
            {stat.best_type && (
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 1 }}>
                ✦ <strong>{stat.best_type}</strong> content gets the most engagement on this platform
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3: Best Times + Cross-platform ROI
// ─────────────────────────────────────────────────────────────────────────────
function BestTimesTab({ bestTimes, crossPlatform }) {
  const theme = useTheme();
  const maxEng = Math.max(...crossPlatform.map(c => c.eng_per_post), 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Best posting times */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>Best Times to Post</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
          Based on when your past posts got the most engagement
        </Typography>
        {Object.keys(bestTimes).length === 0 ? (
          <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>
            Not enough timestamp data yet. Sync more posts to see recommendations.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {Object.entries(bestTimes).map(([platform, time]) => {
              const color = platformColor(platform);
              const icon  = platformIcon(platform);
              const cfg   = PLATFORM_CONFIG[platform?.toLowerCase()] || {};
              return (
                <Grid key={platform} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box sx={{
                    p: 2.5, borderRadius: '16px',
                    border: `1.5px solid ${alpha(color, 0.2)}`,
                    bgcolor: alpha(color, 0.04),
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 34, height: 34, borderRadius: '10px' }}>
                        {icon ? React.cloneElement(icon, { sx: { fontSize: 18 } }) : null}
                      </Avatar>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{cfg.label || platform}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', mb: 0.25 }}>Best Time</Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color }}>{time.label}</Typography>
                      </Box>
                      {time.day && (
                        <Box>
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', mb: 0.25 }}>Best Day</Typography>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color }}>{time.day.slice(0,3)}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Cross-platform ROI */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>Cross-Platform ROI</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
          Engagement per post — which platform gives you the most return for each piece of content
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {crossPlatform.map((cp, i) => {
            const color = platformColor(cp.platform);
            const icon  = platformIcon(cp.platform);
            const cfg   = PLATFORM_CONFIG[cp.platform?.toLowerCase()] || {};
            const pct   = Math.min(100, (cp.eng_per_post / maxEng) * 100);
            return (
              <Box key={cp.platform} sx={{
                p: 2, borderRadius: '14px',
                border: `1.5px solid ${i === 0 ? alpha(color, 0.35) : alpha(theme.palette.divider, 0.08)}`,
                bgcolor: i === 0 ? alpha(color, 0.04) : 'transparent',
                display: 'flex', alignItems: 'center', gap: 2,
              }}>
                <Typography sx={{ fontSize: '1rem', width: 28, textAlign: 'center' }}>
                  {['🥇','🥈','🥉'][i] || `#${i+1}`}
                </Typography>
                <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 32, height: 32, borderRadius: '10px', flexShrink: 0 }}>
                  {icon ? React.cloneElement(icon, { sx: { fontSize: 16 } }) : null}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>{cfg.label || cp.platform}</Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: '0.88rem', color }}>
                      {fmtNum(cp.eng_per_post)} / post
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={pct}
                    sx={{ height: 6, borderRadius: 3, bgcolor: alpha(color, 0.08),
                      '& .MuiLinearProgress-bar': { background: cfg.gradient || color, borderRadius: 3 } }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 4: AI Insights + Deep Analysis
// ─────────────────────────────────────────────────────────────────────────────

/** Small section heading used inside the deep report */
function ReportSection({ icon, title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
        bgcolor: alpha('#6366f1', 0.1), color: '#6366f1',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{subtitle}</Typography>}
      </Box>
    </Box>
  );
}

/** Renders the structured deep-analysis report returned by /rank/ai-deep-analysis */
function DeepReport({ report }) {
  const PERIOD_COLORS = { 'Next 7 days': '#10b981', 'Next 30 days': '#6366f1', 'Next 90 days': '#f59e0b' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

      {/* Executive Summary */}
      {report.executive_summary && (
        <Box sx={{
          p: 3, borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.06))',
          border: '1.5px solid rgba(99,102,241,0.2)',
        }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <AIIcon sx={{ color: '#6366f1', fontSize: 22, mt: 0.25, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.97rem', lineHeight: 1.7, fontWeight: 500, color: 'text.primary' }}>
              {report.executive_summary}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Platform Insights */}
      {report.platform_insights?.length > 0 && (
        <Box>
          <ReportSection icon={<BarChartIcon sx={{ fontSize: 18 }} />} title="Platform Insights" subtitle="What's working and where to focus" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {report.platform_insights.map((pi, i) => {
              const color = platformColor(pi.platform);
              const icon  = platformIcon(pi.platform);
              const score = Math.max(0, Math.min(100, pi.score || 50));
              return (
                <Box key={i} sx={{
                  p: 2.5, borderRadius: '16px',
                  border: `1.5px solid ${alpha(color, 0.2)}`,
                  bgcolor: alpha(color, 0.03),
                }}>
                  {/* Platform header + health score */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ color, display: 'flex', alignItems: 'center' }}>
                      {icon ? React.cloneElement(icon, { sx: { fontSize: 18 } }) : null}
                    </Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', flex: 1 }}>
                      {platformLabel(pi.platform)}
                    </Typography>
                    <Chip
                      label={`Health ${score}/100`}
                      size="small"
                      sx={{ height: 22, fontSize: '0.65rem', fontWeight: 800,
                        bgcolor: alpha(score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444', 0.12),
                        color: score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444',
                        border: 'none' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    {/* Strengths */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
                        ✓ Strengths
                      </Typography>
                      {pi.strengths?.map((s, j) => (
                        <Typography key={j} sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.4, lineHeight: 1.4 }}>• {s}</Typography>
                      ))}
                    </Box>
                    {/* Opportunities */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
                        ↑ Opportunities
                      </Typography>
                      {pi.opportunities?.map((o, j) => (
                        <Typography key={j} sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.4, lineHeight: 1.4 }}>• {o}</Typography>
                      ))}
                    </Box>
                  </Box>
                  {/* Actions */}
                  {pi.actions?.length > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(color, 0.1)}` }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
                        → Actions
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {pi.actions.map((a, j) => (
                          <Chip key={j} label={a} size="small"
                            sx={{ height: 'auto', py: 0.5, fontSize: '0.72rem', fontWeight: 600,
                              bgcolor: alpha(color, 0.08), color, border: 'none',
                              '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 } }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Content Strategy */}
      {report.content_strategy?.length > 0 && (
        <Box>
          <ReportSection icon={<PsychologyIcon sx={{ fontSize: 18 }} />} title="Content Strategy" subtitle="Formats and angles proven to perform for your audience" />
          <Grid container spacing={2}>
            {report.content_strategy.map((cs, i) => {
              const colors = ['#6366f1', '#10b981', '#f59e0b', '#e11d48'];
              const c = colors[i % colors.length];
              return (
                <Grid key={i} size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ p: 2.5, borderRadius: '14px', border: `1.5px solid ${alpha(c, 0.2)}`, bgcolor: alpha(c, 0.04), height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label={cs.format} size="small"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 900, bgcolor: alpha(c, 0.15), color: c, border: 'none' }} />
                      {cs.platforms?.map(p => (
                        <Box key={p} sx={{ display: 'flex', alignItems: 'center', color: platformColor(p) }}>
                          {platformIcon(p) ? React.cloneElement(platformIcon(p), { sx: { fontSize: 13 } }) : null}
                        </Box>
                      ))}
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', mb: 0.5, color: c }}>{cs.title}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.5 }}>{cs.description}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Growth Roadmap */}
      {report.growth_roadmap?.length > 0 && (
        <Box>
          <ReportSection icon={<TimelineIcon sx={{ fontSize: 18 }} />} title="Growth Roadmap" subtitle="Your action plan for the next 90 days" />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {report.growth_roadmap.map((rm, i) => {
              const c = PERIOD_COLORS[rm.period] || '#6366f1';
              return (
                <Box key={i} sx={{ flex: 1, p: 2.5, borderRadius: '14px', border: `1.5px solid ${alpha(c, 0.25)}`, bgcolor: alpha(c, 0.04) }}>
                  <Chip label={rm.period} size="small"
                    sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: alpha(c, 0.15), color: c, border: 'none', mb: 1 }} />
                  <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', mb: 1.25 }}>{rm.title}</Typography>
                  {rm.actions?.map((a, j) => (
                    <Box key={j} sx={{ display: 'flex', gap: 0.75, mb: 0.6 }}>
                      <CheckIcon sx={{ fontSize: 14, color: c, mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.45 }}>{a}</Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Quick Wins */}
      {report.quick_wins?.length > 0 && (
        <Box>
          <ReportSection icon={<BoltIcon sx={{ fontSize: 18 }} />} title="Quick Wins" subtitle="Do these this week for immediate impact" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {report.quick_wins.map((win, i) => (
              <Box key={i} sx={{
                display: 'flex', gap: 1.5, alignItems: 'flex-start',
                p: 2, borderRadius: '12px',
                bgcolor: alpha('#10b981', 0.05),
                border: `1.5px solid ${alpha('#10b981', 0.15)}`,
              }}>
                <Box sx={{
                  width: 24, height: 24, borderRadius: '8px', flexShrink: 0,
                  bgcolor: alpha('#10b981', 0.15), color: '#10b981',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.72rem', fontWeight: 900,
                }}>
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.55, color: 'text.primary', fontWeight: 500 }}>{win}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function AIInsightsTab({ insights, intelligenceData, deepAnalysis, deepLoading, deepError, onGenerate }) {
  const colors = ['#6366f1', '#10b981', '#f59e0b', '#e11d48'];
  const isQuotaError = deepError?.isQuotaError;

  return (
    <Box>
      {/* Basic insights header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AIIcon sx={{ color: '#6366f1', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>AI Content Strategy</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Personalised recommendations based on your actual post performance data
          </Typography>
        </Box>
      </Box>

      {/* ── Deep Analysis CTA banner (top) ─────────────────────────────── */}
      {!deepAnalysis && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          p: 2, mb: 3, borderRadius: '14px',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(139,92,246,0.05))',
          border: `1.5px solid ${alpha('#6366f1', 0.2)}`,
        }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
            bgcolor: alpha('#6366f1', 0.12), color: '#6366f1',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isQuotaError ? <LockIcon sx={{ fontSize: 20 }} /> : <PsychologyIcon sx={{ fontSize: 20 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isQuotaError ? (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>AI Credits Exhausted</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  Upgrade your plan or top up credits to unlock deep analysis.
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                  Want a deeper analysis?
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  Full strategy report: health scores, content recommendations, 90-day roadmap &amp; quick wins.{' '}
                  <span style={{ color: '#9ca3af' }}>Uses 1 AI credit.</span>
                </Typography>
                {deepError && !isQuotaError && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#ef4444', mt: 0.25 }}>
                    {deepError?.message || String(deepError)}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {isQuotaError ? (
            <Button variant="contained" size="small"
              onClick={() => window.location.href = '/app/billing'}
              sx={{ borderRadius: '9px', px: 2.5, fontWeight: 800, flexShrink: 0,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}>
              Upgrade
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={deepLoading ? <CircularProgress size={14} color="inherit" /> : <AIIcon sx={{ fontSize: '16px !important' }} />}
              onClick={onGenerate}
              disabled={deepLoading}
              sx={{
                borderRadius: '9px', px: 2.5, fontWeight: 800, flexShrink: 0,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
              }}
            >
              {deepLoading ? 'Generating…' : 'Generate Report'}
            </Button>
          )}
        </Box>
      )}

      {/* Quick heuristic insights */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {insights.map((insight, i) => (
          <Box key={i} sx={{
            p: 2.5, borderRadius: '14px',
            border: `1.5px solid ${alpha(colors[i % colors.length], 0.2)}`,
            bgcolor: alpha(colors[i % colors.length], 0.04),
            display: 'flex', gap: 2, alignItems: 'flex-start',
          }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '9px', flexShrink: 0,
              bgcolor: alpha(colors[i % colors.length], 0.12),
              color: colors[i % colors.length],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.78rem', fontWeight: 900,
            }}>
              {i + 1}
            </Box>
            <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'text.primary', fontWeight: 500 }}>
              {insight}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Deep Analysis Result ────────────────────────────────────────── */}
      {deepAnalysis && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
            <Chip
              icon={<AIIcon sx={{ fontSize: '13px !important' }} />}
              label="AI DEEP ANALYSIS"
              size="small"
              sx={{ height: 22, fontSize: '0.62rem', fontWeight: 900, letterSpacing: '0.06em',
                bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', border: `1px solid ${alpha('#6366f1', 0.2)}`,
                '& .MuiChip-icon': { color: 'inherit' } }}
            />
            <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
          </Box>
          <DeepReport report={deepAnalysis} />
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={onGenerate} disabled={deepLoading}
              sx={{ color: 'text.disabled', fontSize: '0.75rem', fontWeight: 700 }}>
              {deepLoading ? 'Regenerating…' : 'Regenerate'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const RankContent = () => {
  const theme    = useTheme();
  const navigate = useNavigate();
  const [tab,          setTab]          = useState(0);
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [deepAnalysis, setDeepAnalysis] = useState(null);
  const [deepLoading,  setDeepLoading]  = useState(false);
  const [deepError,    setDeepError]    = useState(null);
  const hasFetched = useRef(false);

  const fetchIntelligence = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await rankApi.getIntelligence();
      setData(result);
    } catch (err) {
      setError(err?.message || 'Failed to load rankings');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDeepAnalysis = async () => {
    if (!data) return;
    setDeepLoading(true);
    setDeepError(null);
    try {
      const report = await rankApi.generateDeepAnalysis({
        platform_stats:  data.platform_stats  || [],
        top_posts:       data.top_posts        || [],
        best_times:      data.best_times       || {},
        cross_platform:  data.cross_platform   || [],
      });
      setDeepAnalysis(report);
    } catch (err) {
      setDeepError(err);
    } finally {
      setDeepLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchIntelligence();
    }
  }, []);

  const errorMsg = error;
  const isNotConnected = !!errorMsg.match(/no .*accounts|link.*platforms|NO_ACCOUNTS_CONNECTED/i);

  if (isNotConnected) {
    return (
      <Box sx={{ py: 6 }}>
        <ConnectPlatformsCTA
          title="Rankings Locked"
          description="Connect your social media platforms in Settings to unlock AI-powered content intelligence."
          buttonText="Link Your Platforms"
        />
      </Box>
    );
  }

  const hasData     = data && data.total_posts > 0;
  const topPosts    = data?.top_posts      || [];
  const platStats   = data?.platform_stats || [];
  const bestTimes   = data?.best_times     || {};
  const crossPlat   = data?.cross_platform || [];
  const aiInsights  = data?.ai_insights    || [];
  const totalPosts  = data?.total_posts    || 0;
  const totalViews  = platStats.reduce((s, p) => s + p.total_views, 0);
  const totalEng    = platStats.reduce((s, p) => s + p.total_likes + p.total_comments + p.total_shares, 0);

  const TAB_LABELS = [
    { label: 'Top Posts',    icon: <TrophyIcon sx={{ fontSize: 16 }} /> },
    { label: 'By Platform',  icon: <BarChartIcon sx={{ fontSize: 16 }} /> },
    { label: 'Best Times',   icon: <CalIcon sx={{ fontSize: 16 }} /> },
    { label: 'AI Strategy',  icon: <AIIcon sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 2, mb: 4 }}>
        <Box>
          <Chip
            icon={<AIIcon sx={{ fontSize: '14px !important' }} />}
            label="CONTENT INTELLIGENCE"
            size="small"
            sx={{
              mb: 1, height: 24, fontSize: '0.65rem', fontWeight: 900, letterSpacing: '0.08em',
              bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 0.5 }}>
            Content Rankings
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            What's working, what to post next, and when to post it
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={fetchIntelligence}
          disabled={loading}
          sx={{
            borderRadius: '12px', px: 3, py: 1.25, fontWeight: 800, flexShrink: 0,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
          }}
        >
          {loading ? 'Analyzing...' : 'Refresh'}
        </Button>
      </Box>

      {loading && !data ? (
        <PageSkeleton />
      ) : !hasData ? (
        /* ── Empty state ──────────────────────────────────────────────────── */
        <Box sx={{
          textAlign: 'center', py: 10, borderRadius: '24px',
          border: `2px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '20px', bgcolor: alpha(theme.palette.primary.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
            <SyncIcon sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.4) }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>No posts synced yet</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, maxWidth: 400, mx: 'auto', lineHeight: 1.65 }}>
            Your platforms are connected. Sync your posts first to unlock top post rankings, content type analysis, best time to post, and AI strategy recommendations.
          </Typography>
          <Button variant="contained" startIcon={<SyncIcon />} onClick={() => navigate('/app/past-posts')}
            sx={{ borderRadius: '12px', px: 3, py: 1.25, fontWeight: 800, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            Sync Posts Now
          </Button>
        </Box>
      ) : (
        <>
          {/* ── Summary strip ──────────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
            <StatCard icon={<BarChartIcon />}   label="Platforms"   value={platStats.length}    color="#6366f1" sub="connected" />
            <StatCard icon={<TrophyIcon />}      label="Total Posts" value={fmtNum(totalPosts)}  color="#0ea5e9" sub="synced" />
            <StatCard icon={<ViewsIcon />}       label="Total Views" value={fmtNum(totalViews)}  color="#10b981" sub="across all" />
            <StatCard icon={<LikesIcon />}       label="Engagement"  value={fmtNum(totalEng)}   color="#f59e0b" sub="likes+comments+shares" />
          </Box>

          {/* ── Tabs ───────────────────────────────────────────────────────── */}
          <Box sx={{
            borderBottom: 1, borderColor: 'divider', mb: 0,
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' },
          }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              {TAB_LABELS.map((t, i) => (
                <Tab key={i} icon={t.icon} iconPosition="start" label={t.label}
                  sx={{ fontWeight: 800, fontSize: '0.82rem', textTransform: 'none', minHeight: 48, gap: 0.5,
                    '&.Mui-selected': { color: theme.palette.primary.main } }} />
              ))}
            </Tabs>
          </Box>

          {/* ── Tab 0: Top Posts ───────────────────────────────────────────── */}
          <TabPanel value={tab} index={0}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
              Your {topPosts.length} best-performing posts across all platforms, ranked by engagement score (likes + comments×2 + shares×3)
            </Typography>
            <Grid container spacing={2.5}>
              {topPosts.map((post, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <TopPostCard post={post} rank={i + 1} />
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* ── Tab 1: Platform analysis ───────────────────────────────────── */}
          <TabPanel value={tab} index={1}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
              Average metrics per post and content type breakdown — see what format and platform is worth your effort
            </Typography>
            <Grid container spacing={2.5}>
              {platStats.map(stat => (
                <Grid key={stat.platform} size={{ xs: 12, md: 6, lg: 4 }}>
                  <PlatformAnalysisCard stat={stat} />
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* ── Tab 2: Best Times ──────────────────────────────────────────── */}
          <TabPanel value={tab} index={2}>
            <BestTimesTab bestTimes={bestTimes} crossPlatform={crossPlat} />
          </TabPanel>

          {/* ── Tab 3: AI Strategy ─────────────────────────────────────────── */}
          <TabPanel value={tab} index={3}>
            <AIInsightsTab
              insights={aiInsights}
              deepAnalysis={deepAnalysis}
              deepLoading={deepLoading}
              deepError={deepError}
              onGenerate={handleGenerateDeepAnalysis}
            />
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default RankContent;
